import { CalendarClock } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/format-currency'
import type { PartnerInstallmentPlan } from '@/types'

export const DEFAULT_INSTALLMENT_PLAN: PartnerInstallmentPlan = { parcelas: 1, intervalosMeses: [] }

export type InstallmentChainLevel = { parceiroId: string; nome: string; nivel: number }

const MAX_PAYMENTS = 12

const INTERVAL_OPTIONS = [
  { months: 1, label: '30 dias' },
  { months: 2, label: '60 dias' },
  { months: 3, label: '90 dias' },
  { months: 4, label: '120 dias' },
  { months: 6, label: '180 dias' },
]

const SELECT_CLASS =
  'rounded-md border border-[var(--border)] bg-[var(--bg-main)] px-2.5 py-1.5 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-primary)] disabled:opacity-60'

/** Padrão: 1ª parcela para o parceiro do deal, 2ª para quem o indicou...; extras para o principal (topo). */
export function defaultBeneficiaryAt(chain: InstallmentChainLevel[], index: number): string | undefined {
  return chain[Math.min(index, chain.length - 1)]?.parceiroId
}

/** Plano padrão para a cadeia: uma mensalidade por parceiro (mínimo 2). */
export function defaultPlanForChain(chain: InstallmentChainLevel[]): PartnerInstallmentPlan {
  const parcelas = Math.max(2, chain.length)
  return resizeInstallmentPlan({ parcelas: 0, intervalosMeses: [] }, parcelas, chain)
}

export function resizeInstallmentPlan(
  plan: PartnerInstallmentPlan,
  parcelas: number,
  chain: InstallmentChainLevel[] = [],
): PartnerInstallmentPlan {
  const intervals = plan.intervalosMeses.slice(0, Math.max(0, parcelas - 1))
  while (intervals.length < parcelas - 1) intervals.push(intervals[intervals.length - 1] ?? 1)
  if (chain.length === 0) return { parcelas, intervalosMeses: intervals }

  const valid = new Set(chain.map((level) => level.parceiroId))
  const beneficiarios = Array.from({ length: parcelas }, (_, index) => {
    const current = plan.beneficiarios?.[index]
    return current && valid.has(current) ? current : (defaultBeneficiaryAt(chain, index) as string)
  })
  return { parcelas, intervalosMeses: intervals, beneficiarios }
}

function addMonths(date: Date, months: number) {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1)
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(date.getDate(), lastDay))
  return target
}

export function installmentScheduleFrom(first: Date, plan: PartnerInstallmentPlan): Date[] {
  const dates = [first]
  for (const months of plan.intervalosMeses) dates.push(addMonths(dates[dates.length - 1], months))
  return dates
}

export function describeInstallmentPlan(plan: PartnerInstallmentPlan): string {
  if (plan.parcelas === 1) return '1 mensalidade, paga junto com o 1º pagamento do cliente'
  const intervals = plan.intervalosMeses.map((m) => `${m * 30}`)
  const unique = new Set(intervals)
  const spacing = unique.size === 1 ? `a cada ${intervals[0]} dias` : `intervalos de ${intervals.join(' / ')} dias`
  return `${plan.parcelas} mensalidades, ${spacing}`
}

interface PartnerInstallmentPlanFieldsProps {
  value: PartnerInstallmentPlan
  onChange: (plan: PartnerInstallmentPlan) => void
  /** Cadeia do parceiro (nível 1 = parceiro do deal); habilita escolher quem recebe cada parcela. */
  chain?: InstallmentChainLevel[]
  disabled?: boolean
  /** Valor de cada parcela (uma mensalidade cheia); só para pré-visualização. */
  valorMensal?: number
  /** Data do 1º pagamento do cliente, quando já conhecida. */
  firstPaymentDate?: string | null
}

export function PartnerInstallmentPlanFields({
  value,
  onChange,
  chain = [],
  disabled = false,
  valorMensal,
  firstPaymentDate,
}: PartnerInstallmentPlanFieldsProps) {
  const today = new Date()
  const exampleFirst = firstPaymentDate
    ? new Date(`${firstPaymentDate.slice(0, 10)}T12:00:00`)
    : new Date(today.getFullYear(), today.getMonth() + 1, 10)
  const schedule = installmentScheduleFrom(exampleFirst, value)
  const hasChain = chain.length > 0

  const setInterval = (index: number, months: number) => {
    const intervals = [...value.intervalosMeses]
    intervals[index] = months
    onChange({ ...value, intervalosMeses: intervals })
  }

  const setBeneficiary = (index: number, parceiroId: string) => {
    const current = resizeInstallmentPlan(value, value.parcelas, chain).beneficiarios ?? []
    const beneficiarios = [...current]
    beneficiarios[index] = parceiroId
    onChange({ ...value, beneficiarios })
  }

  const beneficiaryAt = (index: number) => {
    const chosen = value.beneficiarios?.[index]
    return chain.some((level) => level.parceiroId === chosen) ? chosen : defaultBeneficiaryAt(chain, index)
  }

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <label className='text-[11px] font-medium uppercase tracking-wider text-[var(--font-color-label)]' htmlFor='installment-count'>
          Mensalidades destinadas aos parceiros
        </label>
        <select
          id='installment-count'
          value={value.parcelas}
          disabled={disabled}
          onChange={(e) => onChange(resizeInstallmentPlan(value, Number(e.target.value), chain))}
          className={SELECT_CLASS}
        >
          {Array.from({ length: MAX_PAYMENTS }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n} {n === 1 ? 'pagamento' : 'pagamentos'}
            </option>
          ))}
        </select>
      </div>

      <ol className='divide-y divide-[var(--border)] rounded-lg border border-[var(--border)]'>
        {schedule.map((date, index) => (
          <li key={index} className='space-y-2 px-3 py-2 text-xs'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <span className='flex items-center gap-2'>
                <span className='rounded bg-[var(--glass-bg)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--text-muted)]'>
                  {index + 1}º
                </span>
                {index === 0 ? (
                  <span className='text-[var(--text-primary)]'>No 1º pagamento do cliente</span>
                ) : (
                  <span className='flex items-center gap-1.5 text-[var(--text-primary)]'>
                    <select
                      aria-label={`Intervalo do ${index + 1}º pagamento`}
                      value={value.intervalosMeses[index - 1] ?? 1}
                      disabled={disabled}
                      onChange={(e) => setInterval(index - 1, Number(e.target.value))}
                      className={SELECT_CLASS}
                    >
                      {INTERVAL_OPTIONS.map((option) => (
                        <option key={option.months} value={option.months}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    após o {index}º
                  </span>
                )}
              </span>
              <span className={cn('text-[11px]', firstPaymentDate ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}>
                {firstPaymentDate ? '' : 'ex.: '}
                {date.toLocaleDateString('pt-BR')}
              </span>
            </div>
            {hasChain && (
              <div className='flex flex-wrap items-center gap-1.5 pl-7 text-[var(--text-muted)]'>
                <span>para</span>
                <select
                  aria-label={`Parceiro do ${index + 1}º pagamento`}
                  value={beneficiaryAt(index) ?? ''}
                  disabled={disabled}
                  onChange={(e) => setBeneficiary(index, e.target.value)}
                  className={SELECT_CLASS}
                >
                  {chain.map((level) => (
                    <option key={level.parceiroId} value={level.parceiroId}>
                      {level.nome} · N{level.nivel}
                      {level.nivel === 1 ? ' (parceiro do deal)' : level.nivel === chain.length ? ' (principal)' : ''}
                    </option>
                  ))}
                </select>
                {valorMensal && valorMensal > 0 ? (
                  <span className='font-semibold text-emerald-500'>{formatCurrency(valorMensal)}</span>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ol>

      <p className='flex items-start gap-1.5 text-[11px] leading-relaxed text-[var(--text-muted)]'>
        <CalendarClock size={12} className='mt-0.5 shrink-0' />
        <span>
          Cada pagamento é uma mensalidade cheia para um parceiro
          {valorMensal && valorMensal > 0 ? ` (${formatCurrency(valorMensal)})` : ''}. Por padrão o 1º vai para o
          parceiro do deal, o 2º para quem o indicou, e assim por diante.
          {!firstPaymentDate &&
            ' O 1º sai na data do 1º pagamento do cliente (ex.: fechou dia 5, cliente paga dia 10 do mês seguinte).'}
        </span>
      </p>
    </div>
  )
}
